/** Following cases through: my cases, the case timeline, investigations, and case files shared with the solicitor. */
import { CaseWorkspace } from './Intel.tsx';
import { useEffect, useRef, useState } from 'react';
import { CASE_FILE_ITEMS, CASE_OUTCOME_LABEL, CASE_STAGE_LABEL, CASE_STAGES, CASE_STATUS_LABEL, FOLLOWUP_DAYS, type CaseOutcome, type CaseStage, type CaseStatus } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link, useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, fileBase64, linkTo, when } from './common.tsx';
import { PhotoFolders } from './Photos.tsx';

function Stages({ stage, c }: { stage: CaseStage; c?: any }) {
  const i = CASE_STAGES.indexOf(stage);
  return (
    <ol className="stages" aria-label={`Stage: ${CASE_STAGE_LABEL[stage]}`}>
      {CASE_STAGES.map((s, k) => (
        <li key={s} className={k < i ? 'done' : k === i ? 'cur' : ''}>
          <span>{CASE_STAGE_LABEL[s]}</span>
          {k === i && c && (
            <em>
              {s === 'investigations' && c.investigator_name ? c.investigator_name : s === 'court' && c.next_court ? `Next ${day(c.next_court)}` : s === 'done' && c.outcome ? CASE_OUTCOME_LABEL[c.outcome as CaseOutcome] : s === 'done' && c.case_status ? CASE_STATUS_LABEL[c.case_status as CaseStatus] : ''}
            </em>
          )}
        </li>
      ))}
    </ol>
  );
}

const title = (c: any) => c.summary?.title || c.summary?.offenses?.[0] || 'Report';

/** Filters for any list of cases: where it is, report status, when, and newest or oldest first. */
const REPORT_STATUS: Record<string, string> = { draft: 'Draft', returned: 'Sent back', submitted: 'Waiting for approval', approved: 'Approved' };
function useCaseFilter(cases: any[]) {
  const [f, setF] = useState({ stage: '', status: '', within: '', caseStatus: '', sort: 'new' });
  const shown = cases
    .filter((c) => !f.stage || c.stage === f.stage)
    .filter((c) => !f.status || c.status === f.status)
    .filter((c) => !f.caseStatus || (f.caseStatus === 'none' ? !c.case_status : c.case_status === f.caseStatus))
    .filter((c) => !f.within || +new Date(c.occurred_at ?? c.created_at) >= Date.now() - Number(f.within) * 86_400_000)
    .sort((a, b) => (f.sort === 'new' ? -1 : 1) * (+new Date(a.occurred_at ?? a.created_at) - +new Date(b.occurred_at ?? b.created_at)));
  const any = !!(f.stage || f.status || f.within || f.caseStatus || f.sort !== 'new');
  const bar = (
    <div className="case-filters">
      <select value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })} aria-label="Filter by where it is">
        <option value="">Anywhere</option>
        {CASE_STAGES.map((s) => (
          <option key={s} value={s}>
            {CASE_STAGE_LABEL[s]}
          </option>
        ))}
      </select>
      <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} aria-label="Filter by report status">
        <option value="">Any report status</option>
        {Object.entries(REPORT_STATUS).map(([k, l]) => (
          <option key={k} value={k}>
            {l}
          </option>
        ))}
      </select>
      <select value={f.caseStatus} onChange={(e) => setF({ ...f, caseStatus: e.target.value })} aria-label="Filter by case status">
        <option value="">Any case status</option>
        <option value="none">No case status yet</option>
        {Object.entries(CASE_STATUS_LABEL).map(([k, l]) => (
          <option key={k} value={k}>
            {l as string}
          </option>
        ))}
      </select>
      <select value={f.within} onChange={(e) => setF({ ...f, within: e.target.value })} aria-label="Filter by when it happened">
        <option value="">Any time</option>
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
        <option value="365">Last year</option>
      </select>
      <select value={f.sort} onChange={(e) => setF({ ...f, sort: e.target.value })} aria-label="Sort cases">
        <option value="new">Newest first</option>
        <option value="old">Oldest first</option>
      </select>
      {any && (
        <button type="button" className="btn link small" onClick={() => setF({ stage: '', status: '', within: '', caseStatus: '', sort: 'new' })}>
          Clear filters
        </button>
      )}
      <span className="small muted">
        {shown.length} of {cases.length}
      </span>
    </div>
  );
  return { shown, bar };
}

export function MyCasesPage() {
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const r = useLoad(() => api.get<any>(`/api/my-cases${query ? `?q=${encodeURIComponent(query)}` : ''}`), [query]);
  const all: any[] = r.data?.cases ?? [];
  const { shown: cases, bar } = useCaseFilter(all);
  return (
    <div className="ops stack">
      <PageHeader crumb="Reports" title="My cases" sub="Every case you wrote or worked, and where it is now — investigations, the solicitor, court, and how it ended." />
      <div className="filterbar">
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Case number, offense, name or address" aria-label="Search my cases" />
      </div>
      {bar}
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !all.length ? (
        <EmptyState icon="route" title="No cases yet">
          Reports you write show up here so you can follow them all the way through.
        </EmptyState>
      ) : !cases.length ? (
        <EmptyState icon="route" title="No cases match these filters" />
      ) : (
        <div className="case-list">
          {cases.map((c) => (
            <button key={c.id} type="button" className="card case-card" onClick={() => navigate(at(`/cases/${c.id}`))}>
              <div className="row">
                <strong className="mono">{c.case_number}</strong>
                <span>{title(c)}</span>
                <span className="spacer" />
                {c.evidence_open > 0 && <Badge tone="info">{c.evidence_open} in evidence</Badge>}
                {c.status === 'draft' && <Badge tone="neutral">Draft</Badge>}
                {c.status === 'returned' && <Badge tone="warn">Sent back to you</Badge>}
                {c.status === 'submitted' && <Badge tone="info">Waiting for approval</Badge>}
              </div>
              <Stages stage={c.stage} c={c} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const KIND_ICON: Record<string, string> = { report: 'report', supplement: 'report', evidence: 'evidence', investigation: 'search', solicitor: 'folderShare', court: 'gavel', outcome: 'check' };

export function CaseTrack({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/cases/${id}/track`), [id]);
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const c = r.data.case;
  const base = location.pathname.startsWith('/field') ? '/field/reports' : '/reports';
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<Link to={at('/cases')}>My cases</Link>}
        title={`Case ${c.case_number}`}
        sub={`${title(c)} · written by ${c.author_name}`}
        actions={
          <Link className="btn" to={`${base}/${c.id}`}>
            Open the report
          </Link>
        }
      />
      <Card>
        <Stages stage={c.stage} c={c} />
        <dl className="kv cols">
          <dt>Investigator</dt>
          <dd>{c.investigator_name ?? <span className="muted">Not adopted by investigations</span>}</dd>
          <dt>Case status</dt>
          <dd>
            {c.case_status ? CASE_STATUS_LABEL[c.case_status as CaseStatus] : <span className="muted">—</span>}
            {c.case_status_note && <div className="small muted">{c.case_status_note}</div>}
          </dd>
          <dt>Solicitor</dt>
          <dd>{c.case_file_id ? <Link to={at(`/case-files/${c.case_file_id}`)}>{c.outcome ? CASE_OUTCOME_LABEL[c.outcome as CaseOutcome] : `Shared ${day(c.shared_at)}`}</Link> : <span className="muted">Not shared yet</span>}</dd>
          <dt>Next court date</dt>
          <dd>{c.next_court ? when(c.next_court, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : <span className="muted">—</span>}</dd>
        </dl>
      </Card>
      {r.data.canInvestigate && <InvestigationBox c={c} investigators={r.data.investigators} onSaved={r.reload} />}
      {!c.case_file_id && <ShareBox caseNumber={c.case_number} onSaved={r.reload} />}
      <CaseWorkspace id={c.id} onChange={r.reload} />
      <PhotoFolders incidentId={c.id} embedded />
      <Card title="Everything that’s happened">
        <ol className="timeline">
          {r.data.timeline.map((t: any, i: number) => {
            const href = linkTo(t.link);
            const future = +new Date(t.at) > Date.now();
            return (
              <li key={i} className={`k-${t.kind} ${future ? 'future' : ''}`}>
                <span className="tl-dot" aria-hidden />
                <div>
                  <strong>{href ? <Link to={href}>{t.title}</Link> : t.title}</strong>
                  {t.detail && <div className="small">{t.detail}</div>}
                  <div className="small muted">
                    {when(t.at, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    {t.by ? ` · ${t.by}` : ''}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </Card>
    </div>
  );
}

function InvestigationBox({ c, investigators, onSaved }: { c: any; investigators: any[]; onSaved: () => void }) {
  const { me } = useAuth();
  const [inv, setInv] = useState<string>(c.investigator_id ?? '');
  const [status, setStatus] = useState<string>(c.case_status ?? '');
  const [note, setNote] = useState('');
  const a = useAction();
  const save = (body: Record<string, unknown>) => a.run(async () => (await api.post(`/api/cases/${c.id}/investigation`, body), setNote(''), onSaved()));
  return (
    <Card title="Investigations">
      {!c.investigator_id && (
        <p>
          <button className="btn primary" disabled={a.busy} onClick={() => save({ investigatorId: me!.user.id })}>
            Adopt this case
          </button>{' '}
          <span className="small muted">Assigns it to you and marks it an active investigation. The deputy who wrote it is told.</span>
        </p>
      )}
      <div className="grid-3">
        <label>
          Investigator
          <select value={inv} onChange={(e) => setInv(e.target.value)}>
            <option value="">— none —</option>
            {investigators.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.unit ? ` (${p.unit})` : ''}
              </option>
            ))}
          </select>
        </label>
        <label>
          Case status
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">—</option>
            {Object.entries(CASE_STATUS_LABEL).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label>
          Note to the deputy
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
        </label>
      </div>
      <button className="btn" disabled={a.busy || (inv === (c.investigator_id ?? '') && status === (c.case_status ?? '') && !note.trim())} onClick={() => save({ investigatorId: inv || null, status: status || null, note: note || undefined })}>
        Save and tell the deputy
      </button>
      <ErrorBox error={a.error} />
    </Card>
  );
}

function ShareBox({ caseNumber, onSaved }: { caseNumber: string; onSaved: () => void }) {
  const { can } = useAuth();
  const { navigate } = useRouter();
  const [open, setOpen] = useState(false);
  if (!can('casefiles.share')) return null;
  return open ? (
    <ShareForm caseNumber={caseNumber} onClose={() => setOpen(false)} onSaved={(id) => (onSaved(), navigate(at(`/case-files/${id}`)))} />
  ) : (
    <div className="row gap-sm">
      <button className="btn" onClick={() => setOpen(true)}>
        Share with the solicitor…
      </button>
    </div>
  );
}

export function InvestigationsPage() {
  const { navigate } = useRouter();
  const [tab, setTab] = useState<'new' | 'mine' | 'open' | 'all'>('new');
  const r = useLoad(() => api.get<any>(`/api/investigations?tab=${tab}`), [tab]);
  const [iq, setIq] = useState('');
  const all: any[] = (r.data?.cases ?? []).filter((c: any) => {
    const s = iq.trim().toLowerCase();
    return !s || [c.case_number, title(c), c.author_name, c.investigator_name].some((x) => x && String(x).toLowerCase().includes(s));
  });
  const { shown: cases, bar } = useCaseFilter(all);
  return (
    <div className="ops stack">
      <PageHeader crumb="Reports" title="Investigations" sub="Approved cases waiting for an investigator, and the cases you’re working." />
      <div className="tabs" role="tablist">
        {(
          [
            ['new', 'Not adopted yet'],
            ['mine', 'My cases'],
            ['open', 'All open investigations'],
            ['all', 'All reports'],
          ] as const
        ).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
            {l}
          </button>
        ))}
      </div>
      <div className="filterbar">
        <input type="search" className="rp-search" value={iq} onChange={(e) => setIq(e.target.value)} placeholder="Case number, offense, deputy or investigator" aria-label="Search cases" />
      </div>
      {bar}
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !cases.length ? (
        <EmptyState icon="search" title="Nothing here" />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Case #</th>
                  <th>What</th>
                  <th>Deputy</th>
                  <th>Investigator</th>
                  <th>Status</th>
                  <th>Approved</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr key={c.id} className="clickable" onClick={() => navigate(at(`/cases/${c.id}`))}>
                    <td className="mono">{c.case_number}</td>
                    <td>{title(c)}</td>
                    <td>{c.author_name}</td>
                    <td>{c.investigator_name ?? <span className="muted">—</span>}</td>
                    <td>{c.case_status ? CASE_STATUS_LABEL[c.case_status as CaseStatus] : <span className="muted">—</span>}</td>
                    <td>{day(c.approved_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Case files for the solicitor ─────────────────────────────────────────────

function ShareForm({ caseNumber, onClose, onSaved }: { caseNumber?: string; onClose: () => void; onSaved: (id: string) => void }) {
  const [cn, setCn] = useState(caseNumber ?? '');
  const [defendant, setDefendant] = useState('');
  const [charges, setCharges] = useState('');
  const [notes, setNotes] = useState('');
  const [days, setDays] = useState(60);
  const a = useAction();
  return (
    <div className="card stack" role="dialog" aria-label="Share a case with the solicitor">
      <h3>Share a case with the solicitor’s office</h3>
      <div className="grid-3">
        <label>
          Case #
          <input value={cn} onChange={(e) => setCn(e.target.value)} placeholder="2026-000123" readOnly={!!caseNumber} />
        </label>
        <label>
          Defendant
          <input value={defendant} onChange={(e) => setDefendant(e.target.value)} />
        </label>
        <label>
          Ask “did it go to trial?” after
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {FOLLOWUP_DAYS.map((d) => (
              <option key={d} value={d}>
                {d} days (or after the last court date)
              </option>
            ))}
          </select>
        </label>
      </div>
      <label>
        Charges
        <input value={charges} onChange={(e) => setCharges(e.target.value)} placeholder="e.g. PWID methamphetamine; unlawful carry" />
      </label>
      <label>
        Note to the solicitor
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <div className="row gap-sm">
        <button className="btn primary" disabled={!cn.trim() || a.busy} onClick={() => a.run(async () => onSaved((await api.post<any>('/api/case-files', { caseNumber: cn, defendant, charges, notes, followupDays: days })).id))}>
          Share and add files
        </button>
        <button className="btn ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
      <ErrorBox error={a.error} />
    </div>
  );
}

const REASON_LABEL: Record<string, string> = { felony: 'Felony', general_sessions: 'General Sessions', domestic_violence: 'Domestic violence' };
export function CaseFilesPage() {
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const [box, setBox] = useState(() => new URLSearchParams(location.search).get('box') ?? 'all');
  const [sharing, setSharing] = useState(false);
  const r = useLoad(() => api.get<any>(`/api/case-files?box=${box}${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ''}`), [q, box]);
  const files: any[] = r.data?.caseFiles ?? [];
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Court"
        title="Case files"
        sub={r.data?.canRecordOutcome ? 'Cases the sheriff’s office shared with you. Open one to get the files and tell them how it ended.' : 'Cases shared with the solicitor’s office, their files, and how they turned out.'}
        actions={
          r.data?.canShare && (
            <button className="btn primary" onClick={() => setSharing(true)}>
              Share a case
            </button>
          )
        }
      />
      {sharing && <ShareForm onClose={() => setSharing(false)} onSaved={(id) => navigate(at(`/case-files/${id}`))} />}
      <div className="filterbar">
        {r.data && !r.data.solicitor && (
          <div className="seg-ctl" role="tablist" aria-label="Which case files">
            {(
              [
                ['mine', `Mine to finish${r.data.counts?.mine ? ` (${r.data.counts.mine})` : ''}`],
                ['assembling', `Being put together${r.data.counts?.assembling ? ` (${r.data.counts.assembling})` : ''}`],
                ['shared', 'Sent to the solicitor'],
                ['all', 'All'],
              ] as const
            ).map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={box === k} className={box === k ? 'on' : ''} onClick={() => setBox(k)}>
                {l}
              </button>
            ))}
          </div>
        )}
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Case #, defendant or charge" aria-label="Search case files" />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !files.length ? (
        <EmptyState icon="folderShare" title="No case files" />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Case #</th>
                  <th>Defendant</th>
                  <th>Charges</th>
                  <th>Files</th>
                  <th>Next court</th>
                  <th>Outcome</th>
                  <th>Shared / owed by</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.id} className="clickable" onClick={() => navigate(at(`/case-files/${f.id}`))}>
                    <td className="mono">{f.case_number}</td>
                    <td>{f.defendant ?? '—'}</td>
                    <td className="wrap">{f.charges ?? '—'}</td>
                    <td>{f.files}</td>
                    <td>{f.next_court ? day(f.next_court) : '—'}</td>
                    <td>
                      {f.outcome ? (
                        <Badge tone={f.outcome === 'pending' ? 'warn' : 'ok'}>{CASE_OUTCOME_LABEL[f.outcome as CaseOutcome]}</Badge>
                      ) : f.followup_sent_at ? (
                        <Badge tone="warn" dot>
                          Asked — no answer yet
                        </Badge>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {f.status === 'assembling' ? (
                        <>
                          <Badge tone="warn" dot>
                            {f.itemsDone} of {f.itemsTotal} done
                          </Badge>
                          <div className="small muted">
                            {f.assigned_name} · {(f.required_reasons ?? []).map((x: any) => REASON_LABEL[x.why]).filter((v: string, i: number, a: string[]) => a.indexOf(v) === i).join(', ')}
                          </div>
                        </>
                      ) : (
                        <>
                          {day(f.shared_at)}
                          <div className="small muted">{f.shared_by_name}</div>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const size = (n: number) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function CaseFileDetail({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/case-files/${id}`), [id]);
  const pick = useRef<HTMLInputElement>(null);
  const up = useAction();
  const [outcome, setOutcome] = useState<CaseOutcome | null>(null);
  const [note, setNote] = useState('');
  const oc = useAction();
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const c = r.data.caseFile;
  const upload = (files: FileList | null) =>
    up.run(async () => {
      for (const f of Array.from(files ?? [])) await api.post(`/api/case-files/${id}/docs`, { name: f.name, dataBase64: await fileBase64(f) });
      if (pick.current) pick.current.value = '';
      r.reload();
    });
  const asking = c.followup_sent_at && (!c.outcome || c.outcome === 'pending');
  const assembling = c.status === 'assembling';
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<Link to={at('/case-files')}>Case files</Link>}
        title={`Case ${c.case_number}${c.defendant ? ` — ${c.defendant}` : ''}`}
        sub={assembling ? `Being put together · started ${day(c.created_at)}` : `Shared by ${c.shared_by_name} on ${day(c.shared_at)}`}
        actions={
          <button className="btn" onClick={() => api.post(`/api/case-files/${id}/printed`).finally(() => window.print())}>
            Print checklist
          </button>
        }
      />
      {assembling && (c.required_reasons ?? []).length > 0 && (
        <Notice tone="warn">
          A case file is required for this case: {(c.required_reasons as any[]).map((x) => `${x.charge} (${REASON_LABEL[x.why]})`).join('; ')}.
        </Notice>
      )}
      <CaseFileChecklist id={id} data={r.data} onChange={r.reload} />
      {r.data.canRecordOutcome && !assembling && (
        <Card title={asking ? 'Did this case go to trial?' : c.outcome ? 'Outcome' : 'How did this case end?'} className={asking ? 'ask' : ''}>
          {c.outcome && (
            <p>
              <Badge tone={c.outcome === 'pending' ? 'warn' : 'ok'}>{CASE_OUTCOME_LABEL[c.outcome as CaseOutcome]}</Badge> {c.outcome_note} <span className="small muted">({c.outcome_by_name}, {day(c.outcome_at)})</span>
            </p>
          )}
          <div className="outcomes">
            {(Object.keys(CASE_OUTCOME_LABEL) as CaseOutcome[]).map((o) => (
              <button key={o} type="button" className={`btn ${outcome === o ? 'primary' : ''}`} onClick={() => setOutcome(o)}>
                {CASE_OUTCOME_LABEL[o]}
              </button>
            ))}
          </div>
          {outcome && (
            <div className="row gap-sm">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={outcome === 'pending' ? 'e.g. continued to the December term' : 'Sentence, plea details… (optional)'} aria-label="Outcome note" />
              <button className="btn primary" disabled={oc.busy} onClick={() => oc.run(async () => (await api.post(`/api/case-files/${id}/outcome`, { outcome, note }), setOutcome(null), setNote(''), r.reload()))}>
                Save
              </button>
            </div>
          )}
          {r.data.evidenceInCustody > 0 && <p className="small muted">{r.data.evidenceInCustody} item(s) are still in evidence. When you mark the case finished, the evidence room is told.</p>}
          <ErrorBox error={oc.error} />
        </Card>
      )}
      {!r.data.canRecordOutcome && !assembling && (
        <Card title="Outcome">
          {c.outcome ? (
            <p>
              <Badge tone={c.outcome === 'pending' ? 'warn' : 'ok'}>{CASE_OUTCOME_LABEL[c.outcome as CaseOutcome]}</Badge> {c.outcome_note}
            </p>
          ) : (
            <p className="muted">
              Not reported yet.{' '}
              {r.data.followupDue ? `The solicitor will be asked “did this case go to trial?” on ${day(r.data.followupDue)}.` : c.followup_sent_at ? `The solicitor was asked on ${day(c.followup_sent_at)}.` : ''}
            </p>
          )}
        </Card>
      )}
      <Card
        title="Files"
        actions={
          r.data.canUpload && (
            <>
              <input ref={pick} type="file" multiple hidden onChange={(e) => upload(e.target.files)} aria-label="Upload files" />
              <button className="btn primary sm" disabled={up.busy} onClick={() => pick.current?.click()}>
                {up.busy ? 'Uploading…' : 'Upload files'}
              </button>
            </>
          )
        }
      >
        <ErrorBox error={up.error} />
        {r.data.docs.length === 0 ? (
          <p className="muted">No files yet{r.data.canUpload ? ' — upload the report, statements, photos, video or lab results' : ''}.</p>
        ) : (
          <ul className="plain file-list">
            {r.data.docs.map((d: any) => (
              <li key={d.id}>
                <a href={`/api/case-files/docs/${d.id}`} target="_blank" rel="noreferrer">
                  {d.name}
                </a>
                <span className="small muted">
                  {d.item ? `${CASE_FILE_ITEMS.find((x) => x.key === d.item)?.label ?? d.item} · ` : ''}
                  {size(d.size)} · {d.uploaded_by_name} · {day(d.uploaded_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <div className="grid-2">
        <Card title="Charges">
          <p>{c.charges ?? <span className="muted">—</span>}</p>
          {c.notes && <p className="pre small">{c.notes}</p>}
        </Card>
        <Card title="Court dates">
          {r.data.court.length === 0 ? (
            <p className="muted">None scheduled.</p>
          ) : (
            <ul className="plain">
              {r.data.court.map((e: any) => (
                <li key={e.id}>
                  <Link to={at(`/court/${e.id}`)}>{when(e.starts_at, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</Link>
                  <span className="small muted"> · {e.court_name}{e.status !== 'scheduled' ? ` (${e.status})` : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

const foundText = (key: string, f: any) => {
  if (!f) return null;
  if (key === 'report') return `Report in BlueHQ${f.supplements ? ` + ${f.supplements} supplement${f.supplements === 1 ? '' : 's'}` : ''}`;
  if (key === 'warrant') return f.warrants || f.tickets ? `${f.warrants} warrant(s), ${f.tickets} ticket(s) in BlueHQ` : null;
  if (key === 'rights') return f.forms ? `${f.forms} rights form(s) in BlueHQ` : null;
  if (key === 'evidence') return f.items ? `${f.items} evidence item(s) logged` : null;
  if (key === 'photos') return f.photos ? `${f.photos} approved photo(s)` : null;
  return null;
};

/** The folder's checklist: each item done (with files) or N/A; the case summary; send to the solicitor; who opened it. */
function CaseFileChecklist({ id, data, onChange }: { id: string; data: any; onChange: () => void }) {
  const c = data.caseFile;
  const a = useAction();
  const [summary, setSummary] = useState<string | null>(null);
  const pick = useRef<HTMLInputElement>(null);
  const [forItem, setForItem] = useState<string | null>(null);
  const edit = data.canAssemble;
  const set = (key: string, state: 'done' | 'na' | null) => a.run(async () => (await api.post(`/api/case-files/${id}/item`, { key, state }), onChange()));
  const upload = (files: FileList | null) =>
    a.run(async () => {
      for (const f of Array.from(files ?? [])) await api.post(`/api/case-files/${id}/docs`, { name: f.name, dataBase64: await fileBase64(f), item: forItem });
      if (pick.current) pick.current.value = '';
      if (forItem && !(c.checklist ?? {})[forItem]) await api.post(`/api/case-files/${id}/item`, { key: forItem, state: 'done' });
      onChange();
    });
  const docsFor = (key: string) => (data.docs as any[]).filter((d) => d.item === key);
  const done = data.checklist.filter((x: any) => x.state).length;
  return (
    <>
      <Card title={`Case file checklist · ${done} of ${data.checklist.length}`} className="cf-check">
        <input ref={pick} type="file" multiple hidden onChange={(e) => upload(e.target.files)} aria-label="Upload files for this item" />
        <ErrorBox error={a.error} />
        <ol className="cf-items">
          {data.checklist.map((it: any) => {
            const ds = docsFor(it.key);
            const found = foundText(it.key, it.found);
            return (
              <li key={it.key} className={it.state ? `on ${it.state.state}` : ''}>
                <span className="cf-box" aria-hidden>
                  {it.state?.state === 'done' ? '✓' : it.state?.state === 'na' ? 'N/A' : ''}
                </span>
                <div className="cf-body">
                  <strong>{it.label}</strong>
                  {found && <span className="small cf-found"> · {found}</span>}
                  {ds.length > 0 && (
                    <div className="small">
                      {ds.map((d) => (
                        <a key={d.id} href={`/api/case-files/docs/${d.id}`} target="_blank" rel="noreferrer" className="cf-doc">
                          {d.name}
                        </a>
                      ))}
                    </div>
                  )}
                  {it.state && (
                    <div className="small muted">
                      {it.state.state === 'na' ? 'Not applicable' : 'Done'} · {it.state.byName}
                    </div>
                  )}
                </div>
                {edit && it.key !== 'summary' && (
                  <div className="cf-acts no-print">
                    <button type="button" className="btn sm" disabled={a.busy} onClick={() => (setForItem(it.key), setTimeout(() => pick.current?.click(), 0))}>
                      Add file
                    </button>
                    <button type="button" className={`btn sm ${it.state?.state === 'done' ? 'primary' : ''}`} disabled={a.busy} onClick={() => set(it.key, it.state?.state === 'done' ? null : 'done')} aria-pressed={it.state?.state === 'done'}>
                      Done
                    </button>
                    <button type="button" className={`btn sm ${it.state?.state === 'na' ? 'primary' : ''}`} disabled={a.busy} onClick={() => set(it.key, it.state?.state === 'na' ? null : 'na')} aria-pressed={it.state?.state === 'na'}>
                      N/A
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </Card>
      <Card title="Case summary">
        {edit ? (
          <div className="stack sm">
            <textarea rows={6} value={summary ?? c.summary ?? ''} onChange={(e) => setSummary(e.target.value)} placeholder="What happened, what the evidence shows, and what the charges are based on." aria-label="Case summary" />
            <div>
              <button className="btn" disabled={a.busy || summary === null} onClick={() => a.run(async () => (await api.post(`/api/case-files/${id}/summary`, { summary }), setSummary(null), onChange()))}>
                Save summary
              </button>
            </div>
          </div>
        ) : c.summary ? (
          <p className="pre">{c.summary}</p>
        ) : (
          <p className="muted">No summary written.</p>
        )}
      </Card>
      {edit && (
        <Card title="Send to the solicitor">
          {data.missing.length ? (
            <p className="small">
              Still to do (or mark N/A): <strong>{data.missing.join(', ')}</strong>
            </p>
          ) : (
            <p className="small">Everything is done or marked N/A.</p>
          )}
          <button className="btn primary" disabled={a.busy || data.missing.length > 0} onClick={() => window.confirm('Send this case file to the solicitor’s office?') && a.run(async () => (await api.post(`/api/case-files/${id}/send`), onChange()))}>
            Send to the solicitor
          </button>
        </Card>
      )}
      {data.accessLog?.length > 0 && (
        <Card title="Who opened it" className="no-print">
          <table className="table compact">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>What</th>
              </tr>
            </thead>
            <tbody>
              {data.accessLog.map((x: any, i: number) => (
                <tr key={i}>
                  <td>{when(x.at)}</td>
                  <td>
                    {x.name}
                    {x.role === 'solicitor' && (
                      <>
                        {' '}
                        <Badge tone="info">Solicitor</Badge>
                      </>
                    )}
                  </td>
                  <td>{x.action === 'view' ? 'Opened' : x.action === 'print' ? 'Printed' : `Downloaded ${x.doc_name ?? ''}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
